<template>
  <div v-if="$route.name === 'finToolsRateShares'">
    <fin-data-loader :resolver="loadData">
      <template #content>
        <div class="pb-12" style="background-color: #fff;">
          <v-container>
            <v-row>
              <v-col cols="12">
                <h1 class="mb-3 mt-6" style="text-align: center;">Bewerte deine Aktie</h1>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col :offset-sm="aboFeatureEnabled ? 2 : 4" align="right" cols="12" sm="4">
                <p style="text-align: left;">Du interessierst dich für eine Aktie, weißt aber nicht, ob
                  sie gerade günstig zu haben oder vielleicht doch zu teuer ist?<br/>
                  Dann ermittle jetzt den "fairen" Wert der Aktie und vergleiche ihn mit dem aktuellen Börsenkurs.
                </p>

                <fin-subscription-content v-if="!aboFeatureEnabled"
                                          :feature-id="featureIds.SHARE_RATING.id"
                                          :restriction-type="featureRestrictions.UNAVAILABLE"
                                          style="max-width: 700px; text-align: left;"/>
              </v-col>
              <v-col v-if="aboFeatureEnabled" :align="$vuetify.breakpoint.xs ? 'middle' : 'right'" cols="12" sm="4">
                <v-img :max-width="250"
                       :src="require('@/assets/tools/rate/shares/rate-share.svg')"
                       class="my-6"
                       contain/>
              </v-col>
            </v-row>
            <v-row v-if="aboFeatureEnabled">
              <v-col align="center" cols="12">
                <h5 class="mb-4">Welche Aktie möchtest du bewerten?</h5>

                <fin-assets-name-isin-autocomplete
                  :style="{ maxWidth: '420px', marginBottom: shareRatings && shareRatings.length > 0 ? '0' : '150px'}"
                  custom-return-item="id isin name close eps peRatio"
                  :custom-search-and-filter="customSearchAndFilter"
                  @input="(val) => autocompleteChanged(val)"/>
              </v-col>
            </v-row>
          </v-container>
        </div>

        <v-container v-if="shareRatings && shareRatings.length > 0" class="mb-4">
          <v-row>
            <v-col cols="12">
              <h3 class="mb-0 mt-8 mt-md-12">Meine Bewertungen</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col v-for="(shareRating, index) in shareRatings" :key="index" cols="12" lg="3" md="4" sm="6">
              <v-card :loading="modifyAnimation(shareRating.id)"
                      class="elevation-3">
                <template #progress>
                  <v-progress-linear
                    :color="$finColors.primaryDark"
                    height="10"
                    indeterminate
                  />
                </template>

                <router-link v-style:cursor="disableRouteToSRPage(shareRating) === '' ? 'default' : 'pointer'"
                             :to="disableRouteToSRPage(shareRating)"
                             class="rating-link">
                  <v-card-title>
                    <v-tooltip top>
                      <template #activator="{ on }">
                        <h5 v-on="on" class="shortenedText" @click.prevent>{{ shareRating.name }}</h5>
                      </template>
                      <span>{{ shareRating.name }}</span>
                    </v-tooltip>
                  </v-card-title>
                  <v-card-subtitle v-if="shareRating.share">
                    <v-tooltip top>
                      <template #activator="{ on }">
                        <p v-on="on" class="mb-0 subtitle shortenedText" @click.prevent>
                          Aktie: <span>{{ shareRating.share.name }}</span>
                        </p>
                      </template>
                      <span>{{ shareRating.share.name }}</span>
                    </v-tooltip>
                    <span class="mb-0 subtitle subtitle-small">
                      ISIN: <span>{{ shareRating.share.isin }}</span>
                    </span>
                    <fin-lexicon-link icon="mdi-minus-circle"
                                      to="inaktives-wertpapier"
                                      v-if="!shareRating.share.active || !shareRating.share.covered || shareRating.share.delisted"/>

                  </v-card-subtitle>
                  <v-card-text class="pb-2">
                    <v-row no-gutters>
                      <v-col cols="7">
                        <v-icon :size="20" class="mr-1" color="fin-color-primary-dark">icon-money</v-icon>
                        <span class="rating-label">Akt. Wert</span>
                      </v-col>
                      <v-col align="right" cols="5">
                        <span v-if="shareRating.share" class="rating-value">{{
                            shareRating.share.close | finCurrency
                          }}</span>
                        <span v-else class="rating-value" style="font-size: small;">{{ notAvailableText }}</span>
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="7">
                        <v-icon :size="20" class="mr-1" color="fin-color-primary-dark">icon-compare</v-icon>
                        <span class="rating-label">Fairer Wert</span>
                      </v-col>
                      <v-col align="right" cols="5">
                        <span v-if="shareRating.result > 0" class="rating-value">{{
                            shareRating.result | finCurrency
                          }}</span>
                        <span v-else class="rating-value" style="font-size: small;">{{ notAvailableText }}</span>
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="7">
                        <v-icon v-if="shareRating.pricePotential && shareRating.pricePotential > 0"
                                :color="$finColors.primaryDark"
                                :size="20"
                                class="mr-1">
                          icon-value-up
                        </v-icon>
                        <v-icon v-else-if="shareRating.pricePotential && shareRating.pricePotential < 0"
                                :color="$finColors.primaryDark"
                                :size="20"
                                class="mr-1">
                          icon-value-down
                        </v-icon>
                        <v-icon v-else
                                :color="$finColors.primaryDark"
                                :size="20"
                                class="mr-1">
                          icon-ellipse-add
                        </v-icon>
                        <span class="rating-label">Kurspotenzial</span>
                      </v-col>
                      <v-col align="right" cols="5">
                        <span v-if="shareRating.pricePotential && shareRating.result > 0" class="rating-value">{{
                            shareRating.pricePotential | percent
                          }}</span>
                        <span v-else class="rating-value" style="font-size: small;">{{ notAvailableText }}</span>
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="7">
                        <v-icon :size="20" class="mr-1" color="fin-color-primary-dark">icon-duration</v-icon>
                        <span class="rating-label">Zuletzt bewertet</span>
                      </v-col>
                      <v-col align="right" cols="5">
                        <span v-if="shareRating.ratedAt" class="rating-value">{{
                            shareRating.ratedAt | dayShortYearLong
                          }}</span>
                        <span v-else class="rating-value" style="font-size: small;">{{ notAvailableText }}</span>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </router-link>
                <v-card-actions class="rating-actions">
                  <v-row no-gutters>
                    <v-col :style="{ borderRight: '1px solid ' + $finColors.coolGrey }" align="left" cols="2">
                      <v-btn :small="$vuetify.breakpoint.mdAndUp"
                             :x-small="$vuetify.breakpoint.smAndDown"
                             icon
                             @click="deleteRating(shareRating)">
                        <v-icon small>icon-delete</v-icon>
                      </v-btn>
                    </v-col>
                    <v-col cols="5">
                      <v-btn :small="$vuetify.breakpoint.mdAndUp"
                             :disabled="shareRating.inactive || !shareRating.share.active || !shareRating.share.covered || shareRating.share.delisted"
                             :to="{name: 'finToolsRateSharesRatingExec', params: { srId: shareRating.id }}"
                             :x-small="$vuetify.breakpoint.smAndDown"
                             text>
                        <v-icon class="mr-1" small>icon-edit</v-icon>
                        Bearbeiten
                      </v-btn>
                    </v-col>
                    <v-col cols="5">
                      <v-btn :disabled="disableRefreshBtn(shareRating)"
                             :small="$vuetify.breakpoint.mdAndUp"
                             :x-small="$vuetify.breakpoint.smAndDown"
                             text
                             @click="refreshRating(shareRating)">
                        <v-icon class="mr-1" small>icon-refresh</v-icon>
                        Aktualisieren
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
          <v-row v-if="showHintRow">
            <v-col cols="12">
              *) Fairer Wert und Kurspotenzial können bei negativem Bewertungsparameter nicht sinnvoll ermittelt werden.
              <fin-lexicon-link to="grenzen-der-multiplikatormethode">Mehr hierzu findest du im finLexikon
              </fin-lexicon-link>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </fin-data-loader>
  </div>

  <router-view v-else/>
</template>

<script>
import {deleteShareRating, listOwnerShareRatings} from "@/views/protected/finTools/rate/shares/graphql"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import FinDataLoader from "@/components/common/DataLoader"
import {finShareRatingAPI} from "@/js/finAPI"
import FinAssetsNameIsinAutocomplete from "@/components/common/AssetsNameIsinAutocomplete"
import {mapGetters} from "vuex"
import FinLexiconLink from "@/components/common/lexicon/LexiconLink"
import FinSubscriptionContent from "@/components/common/subscription/content"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import PriceModel from "@/mixins/PriceModel"

export default {
  name: "FinRateShares",
  components: {FinSubscriptionContent, FinLexiconLink, FinAssetsNameIsinAutocomplete, FinDataLoader},
  data() {
    return {
      shareRatings: null,
      isLoading: false,
      selectedShareId: null,
      entries: [],
      prevEntries: [],
      search: null,
      prevSearch: null,
      currentModifiedShareRatingId: null,
      notAvailableText: 'nicht verfügbar*',
      featureIds: PRICEMODEL_FEATURE_IDS,
      featureRestrictions: PRICEMODEL_FEATURE_RESTRICTIONS,
    }
  },
  mixins: [PriceModel],
  computed: {
    ...mapGetters('auth', ['userId']),
    showHintRow() {
      const fairValue = this.shareRatings.filter(sr => sr.result < 0).length
      const potential = this.shareRatings.filter(sr => sr.pricePotential < 0).length

      return (fairValue + potential) > 0
    },
    customSearchAndFilter() {
      return `{
        restricted: {eq: false},
        active: {eq: true},
        covered: {eq: true},
        delisted: {eq: false},
        }`
    },
    aboFeatureEnabled() {
      return this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.SHARE_RATING.id)
    },
  },
  methods: {
    async loadData() {
      const result = await API.graphql(graphqlOperation(listOwnerShareRatings, {
        sortDirection: 'DESC'
      }))
      const shareRatings = result.data.listOwnerShareRating
      if (shareRatings && shareRatings.items && shareRatings.items.length > 0) {
        this.shareRatings = shareRatings.items
      }
    },
    modifyAnimation(shareRatingId) {
      if (!this.currentModifiedShareRatingId) {
        return false
      }
      return this.currentModifiedShareRatingId === shareRatingId
    },
    deleteRating(shareRating) {
      this.$confirm({
        title: 'Bewertung löschen',
        text: 'Möchtest du deine gespeicherte Bewertung "' + shareRating.name + '" wirklich löschen?',
        confirmBtn: 'Löschen',
        textWithLexikonLink: false,
        cancelBtn: 'Abbrechen'
      }).then(() => {
        this.currentModifiedShareRatingId = shareRating.id

        API.graphql(graphqlOperation(deleteShareRating, {
          input: {id: shareRating.id}
        }))
          .then(result => {
            // stop loading animation
            this.currentModifiedShareRatingId = null
            this.shareRatings = this.shareRatings.filter(sr => sr.id !== result.data.deleteShareRating.id)
            this.$toasted.global.finToast({
              message: 'Deine Bewertung wurde erfolgreich gelöscht.',
              type: 'success'
            })
          })
          .catch(error => {
            console.log("error deleting share rating " + shareRating.id, error)
            // stop loading animation
            this.currentModifiedShareRatingId = null
            this.$toasted.global.finToast({
              message: 'Beim Löschen deiner Bewertung trat ein Fehler auf. Lade die Seite erneut und wiederhole die Aktion gegebenenfalls.',
              type: 'error'
            })
          })
      })
    },
    refreshRating(shareRating) {
      this.currentModifiedShareRatingId = shareRating.id

      let ratingInput = {
        id: shareRating.id,
        name: shareRating.name,
        shareRatingShareId: shareRating.share.id,
        conditions: shareRating.conditions.map(c => ({
          initial: c.initial,
          financialDataItemId: c.financialDataItemId,
          customSharePositionValue: c.customSharePositionValue,
          basis: c.basis,
          weight: c.weight
        })),
        shareRatingSectorId: shareRating.sector.id,
      }

      finShareRatingAPI.calculateAndSaveShareRating(ratingInput).then(result => {
        this.shareRatings = this.shareRatings.map(sr => sr.id === result.id ? result : sr)
        // stop loading animation
        this.currentModifiedShareRatingId = null
        this.$toasted.global.finToast({
          message: 'Deine Bewertung wurde erfolgreich aktualisiert.',
          type: 'success'
        })
      }).catch(error => {
        console.log("ERROR refreshing share rating", ratingInput, error)
        // stop loading animation
        this.currentModifiedShareRatingId = null
        this.$toasted.global.finToast({
          message: 'Beim Aktualisieren deiner Bewertung trat ein Fehler auf. Lade die Seite erneut und wiederhole die Aktion gegebenenfalls.',
          type: 'error'
        })
      })
    },
    disableRefreshBtn(shareRating) {
      let now = new Date().toDateString()
      let lastRated = new Date(shareRating.ratedAt).toDateString()
      let isNow = lastRated === now

      return shareRating.inactive || !shareRating.share.active || !shareRating.share.covered || shareRating.share.delisted || isNow
    },
    disableRouteToSRPage(shareRating) {
      if (shareRating.inactive || !shareRating.share.active || !shareRating.share.covered || shareRating.share.delisted) {
        return ''
      }

      return {name: 'finToolsRateSharesRatingExec', params: {srId: shareRating.id}}
    },
    autocompleteChanged(val) {
      if (val) {
        this.$router.push({name: 'finToolsRateSharesRatingCreate', params: {sid: val.id}})
      }
    },
  }
}
</script>

<style lang="scss" scoped>
h3 {
  font-family: $body-font-family;
  font-size: 20px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.1;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

h5 {
  font-family: $body-font-family;
  font-size: 18px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  text-align: center;
  color: $fin-color-primary-dark;
}

.subtitle {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  color: $fin-color-cool-grey;
}

.subtitle-small {
  font-size: 12px;
}

.rating-link {
  text-decoration: none;
}

.rating-label {
  font-family: $body-font-family;
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.17;
  letter-spacing: 1px;
  color: $fin-color-cool-grey;
}

.rating-actions {
  display: block;
  text-align: right;
  border-top: 1px solid $fin-color-light-blue-gray;
}

.rating-value {
  font-family: $body-font-family;
  font-size: 14px;
}

.shortenedText {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
</style>
